import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import NumberBox from 'devextreme-react/number-box';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import { TipBodyEditor } from './TipBodyEditor';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  GridFormPopup,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridFormPopupSizeKey
} from '../opsDataGrid';
import { showDeleteConfirmDialog } from '../dialogs/confirmDialog';
import {
  useDashboardTipsJsonImport,
  type DashboardTipImportResult,
  type DashboardTipsJsonImportApi
} from './DashboardTipsJsonImport';

export interface DashboardTipRow {
  id: string;
  title: string;
  body: string;
  category: string;
  minRole: string;
  sortOrder: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  modifiedBy: string;
  modifiedAt: string;
}

export interface DashboardTipUpsertBody {
  title: string;
  body: string;
  category: string;
  minRole: string;
  sortOrder: number;
  isActive: boolean;
}

export interface DashboardTipsAdminApi extends DashboardTipsJsonImportApi {
  list: () => Promise<DashboardTipRow[]>;
  create: (body: DashboardTipUpsertBody) => Promise<DashboardTipRow>;
  update: (id: string, body: DashboardTipUpsertBody) => Promise<DashboardTipRow>;
  delete: (id: string) => Promise<void>;
  uploadAsset: (file: File) => Promise<{ id: string }>;
}

export interface DashboardTipsAdminGridProps {
  api: DashboardTipsAdminApi;
  stateStorageKey: string;
  /** Site-specific audience role values for the minRole dropdown. */
  minRoleOptions: readonly string[];
  defaultMinRole: string;
  /** Resolves a stored tip image id to a fetch URL for this admin surface. */
  assetUrl: (id: string) => string;
}

const GRID_SCHEMA_VERSION = 2;
const GRID_COLUMNS = [
  'title',
  'category',
  'minRole',
  'body',
  'sortOrder',
  'isActive',
  'modifiedAt',
  'modifiedBy'
] as const;

const fieldLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
  color: 'var(--ops-color-text, #111827)'
};

export function DashboardTipsAdminGrid({
  api,
  stateStorageKey,
  minRoleOptions,
  defaultMinRole,
  assetUrl
}: DashboardTipsAdminGridProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [rows, setRows] = useState<DashboardTipRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<DashboardTipUpsertBody>(() => ({
    title: '',
    body: '',
    category: '',
    minRole: defaultMinRole,
    sortOrder: 0,
    isActive: true
  }));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const emptyForm = useCallback(
    (): DashboardTipUpsertBody => ({
      title: '',
      body: '',
      category: '',
      minRole: defaultMinRole,
      sortOrder: 0,
      isActive: true
    }),
    [defaultMinRole]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.list());
      setFormError(null);
    } catch (e) {
      setRows([]);
      setFormError(e instanceof Error ? e.message : 'Failed to load tips');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const { uploadToolbarItem, uploadMenuItem, importPopup } = useDashboardTipsJsonImport({
    api,
    disabled: loading || saving,
    onImported: () => void load()
  });

  const roleItems = useMemo(() => [...minRoleOptions], [minRoleOptions]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setEditorOpen(true);
  };

  const openEdit = (row: DashboardTipRow) => {
    setEditingId(row.id);
    setForm({
      title: row.title,
      body: row.body,
      category: row.category,
      minRole: row.minRole || defaultMinRole,
      sortOrder: row.sortOrder,
      isActive: row.isActive
    });
    setFormError(null);
    setEditorOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body: DashboardTipUpsertBody = {
        title: form.title.trim(),
        body: form.body.trim(),
        category: form.category.trim(),
        minRole: form.minRole.trim() || defaultMinRole,
        sortOrder: form.sortOrder,
        isActive: form.isActive
      };
      if (editingId == null) {
        await api.create(body);
      } else {
        await api.update(editingId, body);
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (editingId == null) return;
    if (!(await showDeleteConfirmDialog('Delete this tip?'))) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.delete(editingId);
      setEditorOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading && rows.length === 0 ? (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
          <LoadIndicator visible height={20} width={20} />
          <span>Loading…</span>
        </div>
      ) : (
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          height="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled
          allowColumnResizing
          allowColumnReordering
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          noDataText="No tips yet — click Add tip or Import JSON."
          onRowDblClick={(e) => onGridRowDblClick(e, openEdit)}
          onKeyDown={(e) => onGridFocusedRowEnter(e, openEdit)}
        >
          <OpsGridStateStoring
            storageKey={stateStorageKey}
            schemaVersion={GRID_SCHEMA_VERSION}
            columns={GRID_COLUMNS}
          />
          <Toolbar>
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void load()}
              refreshHint="Refresh tips"
              newItem={{ hint: 'Add tip', onClick: openCreate }}
              uploadSlot={uploadToolbarItem}
              uploadMenuItem={uploadMenuItem}
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <SearchPanel visible width={240} placeholder="Search tips…" />
          <HeaderFilter visible />
          <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
          <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
          <Paging defaultPageSize={25} />
          <Column dataField="title" caption="Title" minWidth={160} />
          <Column dataField="category" caption="Category" minWidth={100} />
          <Column dataField="minRole" caption="Min role" minWidth={120} />
          <Column dataField="body" caption="Body" minWidth={220} />
          <Column dataField="sortOrder" caption="Sort" dataType="number" width={80} />
          <Column dataField="isActive" caption="Active" dataType="boolean" width={90} />
          <Column dataField="modifiedAt" caption="Modified" dataType="datetime" width={170} />
          <Column dataField="modifiedBy" caption="Modified by" minWidth={120} visible={false} />
        </DataGrid>
      )}

      {importPopup}

      <GridFormPopup
        visible={editorOpen}
        title={editingId == null ? 'Add tip' : 'Edit tip'}
        onClose={() => setEditorOpen(false)}
        sizeStorageKey={opsGridFormPopupSizeKey(stateStorageKey)}
        showSizeChrome
        width={760}
        preferredHeight={620}
        minWidth={640}
        minHeight={480}
        error={formError}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <div>
              {editingId != null ? (
                <Button text="Delete" type="danger" disabled={saving} onClick={() => void remove()} />
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button text="Cancel" disabled={saving} onClick={() => setEditorOpen(false)} />
              <Button
                text={saving ? 'Saving…' : 'Save'}
                type="default"
                disabled={saving}
                onClick={() => void save()}
              />
            </div>
          </div>
        }
      >
        <div className="dashboard-tips-form">
          <label>
            <span style={fieldLabel}>Title</span>
            <TextBox
              value={form.title}
              maxLength={200}
              onValueChanged={(e) => setForm((f) => ({ ...f, title: e.value ?? '' }))}
            />
          </label>
          <div className="dashboard-tips-form__body">
            <span style={fieldLabel}>Body</span>
            <TipBodyEditor
              value={form.body}
              onChange={(body) => setForm((f) => ({ ...f, body }))}
              assetUrl={assetUrl}
              uploadAsset={api.uploadAsset}
              disabled={saving}
            />
          </div>
          <label>
            <span style={fieldLabel}>Category</span>
            <TextBox
              value={form.category}
              maxLength={64}
              onValueChanged={(e) => setForm((f) => ({ ...f, category: e.value ?? '' }))}
            />
          </label>
          <label>
            <span style={fieldLabel}>Min role</span>
            <SelectBox
              items={roleItems}
              value={form.minRole}
              searchEnabled
              onValueChanged={(e) => setForm((f) => ({ ...f, minRole: String(e.value ?? defaultMinRole) }))}
            />
          </label>
          <label>
            <span style={fieldLabel}>Sort order</span>
            <NumberBox
              value={form.sortOrder}
              showSpinButtons
              onValueChanged={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.value ?? 0) }))}
            />
          </label>
          <CheckBox
            text="Active"
            value={form.isActive}
            onValueChanged={(e) => setForm((f) => ({ ...f, isActive: Boolean(e.value) }))}
          />
        </div>
      </GridFormPopup>
    </>
  );
}

export type { DashboardTipImportResult };
